import {
  ContentNode,
  ContentNodeUtils,
  DataTypeLocator,
  ListContentNode,
  ObjectContentNode,
  QueryConfig,
  QueryNode,
  ViewConfig,
  WidgetConfig,
} from '@helo/cms'

export type MainListTableTarget = {
  viewId: string
  viewLabel: string
  viewPath: string
  queryId: string
  connectionLocator: DataTypeLocator
  queryConfig: QueryConfig
  sampleColumnSelector?: DataTypeLocator
}

export type ProbeStatus = 'running' | 'pass' | 'fail' | 'error'

export type ListTableProbeResult = {
  totalCount: number
  itemCount: number
  sampleValue?: string
}

export type ListTableProbeRow = MainListTableTarget & {
  probeQueryId: string
  status: ProbeStatus
  totalCount?: number
  itemCount?: number
  sampleValue?: string
  recordsChecked?: number
  passRecordPath?: string
  passEntityId?: string
  errorMessage?: string
}

const MAIN_LIST_QUERY_IDS = [
  'containerOrderItemList',
  'containersList',
  'fclContainersList',
  'itemsList',
  'ordersList',
  'shipmentsList',
] as const

const isDataTypeLocator = (value: unknown): value is DataTypeLocator =>
  Array.isArray(value) &&
  value.every(
    segment =>
      Array.isArray(segment) &&
      segment.length === 2 &&
      typeof segment[0] === 'string' &&
      typeof segment[1] === 'string',
  )

const getWidgetQueryId = (widget: WidgetConfig): string | undefined => {
  const queryId = widget.state?.widgetProps?.queryId
  return typeof queryId === 'string' && queryId.length > 0 ? queryId : undefined
}

const walkWidget = (
  widget: WidgetConfig,
  visit: (widget: WidgetConfig) => void,
): void => {
  visit(widget)
  widget.children?.forEach(child => walkWidget(child, visit))
  if (widget.slots) {
    Object.values(widget.slots).forEach(slot => walkWidget(slot, visit))
  }
}

const findTableWidget = (
  view: ViewConfig,
  queryId: string,
): WidgetConfig | undefined => {
  let match: WidgetConfig | undefined

  view.widgets.forEach(widget =>
    walkWidget(widget, candidate => {
      if (match || candidate.type !== 'Table') {
        return
      }

      if (getWidgetQueryId(candidate) === queryId) {
        match = candidate
      }
    }),
  )

  return match
}

const getSampleColumnSelector = (
  widget: WidgetConfig,
): DataTypeLocator | undefined => {
  const tableStateConfig = widget.state?.widgetProps?.tableStateConfig as
    | {
        columnConfigs?: Array<{
          selector?: DataTypeLocator
          config?: { selector?: DataTypeLocator }
        }>
      }
    | undefined

  const columnConfigs = tableStateConfig?.columnConfigs ?? []
  const firstColumn = columnConfigs.find(column => {
    const selector = column.selector ?? column.config?.selector
    return isDataTypeLocator(selector)
  })

  return firstColumn?.selector ?? firstColumn?.config?.selector
}

const collectMainListTables = (
  viewConfigs: ViewConfig[],
): MainListTableTarget[] =>
  viewConfigs
    .filter(view => !view.state.path.includes(':'))
    .flatMap(view => {
      const queryConfig = view.queries.find(
        query =>
          query.paginated && MAIN_LIST_QUERY_IDS.includes(query.id as never),
      )

      if (!queryConfig) {
        return []
      }

      const tableWidget = findTableWidget(view, queryConfig.id)
      const connectionLocator =
        tableWidget?.state?.widgetProps?.connectionLocator

      if (!isDataTypeLocator(connectionLocator)) {
        return []
      }

      return [
        {
          viewId: view.id,
          viewLabel: view.state.label,
          viewPath: view.state.path,
          queryId: queryConfig.id,
          connectionLocator,
          queryConfig,
          sampleColumnSelector: tableWidget
            ? getSampleColumnSelector(tableWidget)
            : undefined,
        },
      ]
    })
    .sort((left, right) => left.viewLabel.localeCompare(right.viewLabel))

const createProbeQueryId = (queryId: string): string =>
  `listTableTest_${queryId}`

const formatSampleValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value)
  }

  return JSON.stringify(value)
}

const getSampleValueFromRow = (
  row: ContentNode,
  sampleColumnSelector?: DataTypeLocator,
): string | undefined => {
  if (!sampleColumnSelector) {
    return undefined
  }

  const cellNode = ContentNodeUtils.findContentNode(sampleColumnSelector)(row)
  if (!cellNode) {
    return undefined
  }

  return formatSampleValue(cellNode.data)
}

const extractProbeResult = (
  queryNode: QueryNode | undefined,
  connectionLocator: DataTypeLocator,
  sampleColumnSelector?: DataTypeLocator,
): ListTableProbeResult => {
  if (!queryNode?.queryContentNode) {
    return { totalCount: 0, itemCount: 0 }
  }

  const connectionContentNode = ContentNodeUtils.findContentNode(
    connectionLocator,
  )(queryNode.queryContentNode) as ObjectContentNode | null

  if (!connectionContentNode) {
    return { totalCount: 0, itemCount: 0 }
  }

  const itemsListContentNode = ContentNodeUtils.findContentNode([
    ['key', 'items'],
    ['typeName', 'List'],
  ])(connectionContentNode) as ListContentNode | null

  const payload = connectionContentNode.data as { totalCount?: number } | null
  const items = itemsListContentNode?.items ?? []
  const totalCount = payload?.totalCount ?? items.length
  const firstRow = items[0]

  return {
    totalCount,
    itemCount: items.length,
    sampleValue: firstRow
      ? getSampleValueFromRow(firstRow, sampleColumnSelector)
      : undefined,
  }
}

const toInitialProbeRows = (
  targets: MainListTableTarget[],
): ListTableProbeRow[] =>
  targets.map(target => ({
    ...target,
    probeQueryId: createProbeQueryId(target.queryId),
    status: 'running',
  }))

export const ListTablesTest = {
  MAIN_LIST_QUERY_IDS,
  collectMainListTables,
  createProbeQueryId,
  extractProbeResult,
  toInitialProbeRows,
}
